import {
  getGetSensorHistoryQueryOptions,
  getGetSensorQueryKey,
  useGetSensor,
  useListSensors,
  type SensorsSensor,
} from '@smarthome/api';
import { useSensorStream } from '@smarthome/ui';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, Expand, LineChart, X } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';
import { useDashboardTimeRange } from '../viewer/DashboardTimeRangeContext';

import {
  Modal,
  SegmentedControl,
  SensorChart,
  type SegmentedControlOption,
  type SensorDataset,
} from '@/components/ui';

/**
 * Time range options for the graph
 */
type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

const TIME_RANGE_MS: Record<TimeRange, number> = {
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

type LatestValuePosition = 'left' | 'right';

const LATEST_VALUE_POSITION_OPTIONS: SegmentedControlOption<LatestValuePosition>[] = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
];

const INTEGER_FORMATTER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const ONE_DECIMAL_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatValue(value?: number | null): string {
  if (value === undefined || value === null) return '--';
  if (Number.isInteger(value)) return INTEGER_FORMATTER.format(value);
  return ONE_DECIMAL_FORMATTER.format(value);
}

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
}

/**
 * Line Graph widget config schema
 */
export const lineGraphConfigSchema = z.object({
  sensorHumanIds: z.array(z.string()).min(1, 'At least one sensor is required'),
  timeRangeMode: z.enum(['dashboard', 'fixed']).default('dashboard'),
  timeRange: z.enum(['1h', '6h', '24h', '7d', '30d']).default('24h'),
  showLatestValue: z.boolean().optional(),
  showUnit: z.boolean().optional(),
  showLastUpdated: z.boolean().optional(),
  latestValuePosition: z.enum(['left', 'right']).optional(),
});

export type LineGraphConfig = z.infer<typeof lineGraphConfigSchema>;

/**
 * Multi-sensor selector component
 */
function SensorMultiSelect({
  selectedIds,
  onChange,
  error,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: sensorsData, isLoading } = useListSensors();
  const sensors = sensorsData?.sensors ?? [];

  const selectedSensors = sensors.filter((s) => s.humanId && selectedIds.includes(s.humanId));

  const handleToggle = (sensor: SensorsSensor) => {
    if (!sensor.humanId) return;
    if (selectedIds.includes(sensor.humanId)) {
      onChange(selectedIds.filter((id) => id !== sensor.humanId));
    } else {
      onChange([...selectedIds, sensor.humanId]);
    }
  };

  const handleRemove = (id: string) => {
    onChange(selectedIds.filter((i) => i !== id));
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        Sensors
      </label>

      {/* Selected sensors chips */}
      {selectedSensors.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedSensors.map((sensor) => (
            <span
              key={sensor.humanId}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
            >
              {sensor.name || sensor.humanId}
              <button
                type="button"
                onClick={() => sensor.humanId && handleRemove(sensor.humanId)}
                className="rounded-full p-0.5 hover:bg-blue-200 dark:hover:bg-blue-500/30"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
            error
              ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10'
              : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-white/[0.12]'
          }`}
        >
          <span className="text-gray-500 dark:text-zinc-500">
            {selectedSensors.length > 0
              ? `${selectedSensors.length} sensor${selectedSensors.length > 1 ? 's' : ''} selected`
              : 'Select sensors...'}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  Loading sensors...
                </div>
              ) : sensors.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  No sensors available
                </div>
              ) : (
                sensors.map((sensor) => (
                  <button
                    key={sensor.id}
                    type="button"
                    onClick={() => handleToggle(sensor)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                  >
                    <div>
                      <div className="text-gray-900 dark:text-white">
                        {sensor.name || sensor.humanId}
                      </div>
                      {sensor.name && sensor.humanId && (
                        <div className="text-xs text-gray-500 dark:text-zinc-500">
                          {sensor.humanId}
                        </div>
                      )}
                    </div>
                    {sensor.humanId && selectedIds.includes(sensor.humanId) && (
                      <Check className="h-4 w-4 text-blue-500" />
                    )}
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Config form for Line Graph widget
 */
function LineGraphConfigForm({ config, onChange, errors }: ConfigFormProps<LineGraphConfig>) {
  const isSingleSensor = config.sensorHumanIds.length === 1;
  const showLatestValue = config.showLatestValue ?? false;
  const timeRangeMode = config.timeRangeMode ?? 'fixed';
  return (
    <div className="space-y-4">
      <SensorMultiSelect
        selectedIds={config.sensorHumanIds}
        onChange={(ids) => onChange({ ...config, sensorHumanIds: ids })}
        error={errors?.['sensorHumanIds']}
      />

      {/* Time range mode selector */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Time Range
        </label>
        <select
          value={timeRangeMode}
          onChange={(e) =>
            onChange({
              ...config,
              timeRangeMode: e.target.value as 'dashboard' | 'fixed',
            })
          }
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          <option value="dashboard">Use dashboard timeframe</option>
          <option value="fixed">Fixed timeframe</option>
        </select>
      </div>

      {/* Fixed time range selector — only shown in fixed mode */}
      {timeRangeMode === 'fixed' && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Fixed Time Range
          </label>
          <select
            value={config.timeRange}
            onChange={(e) => onChange({ ...config, timeRange: e.target.value as TimeRange })}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
          >
            {TIME_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Latest value options (single sensor only) */}
      {isSingleSensor && (
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showLatestValue}
              onChange={(e) => onChange({ ...config, showLatestValue: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
            />
            <span className="text-sm text-gray-700 dark:text-zinc-300">Show latest value</span>
          </label>

          {showLatestValue && (
            <>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.showUnit ?? true}
                  onChange={(e) => onChange({ ...config, showUnit: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
                />
                <span className="text-sm text-gray-700 dark:text-zinc-300">Show unit</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.showLastUpdated ?? false}
                  onChange={(e) => onChange({ ...config, showLastUpdated: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
                />
                <span className="text-sm text-gray-700 dark:text-zinc-300">Show last updated</span>
              </label>

              <SegmentedControl
                label="Value position"
                value={config.latestValuePosition ?? 'right'}
                onChange={(value) =>
                  onChange({
                    ...config,
                    latestValuePosition: value,
                  })
                }
                options={LATEST_VALUE_POSITION_OPTIONS}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renderer for Line Graph widget
 */
const LineGraphRenderer = memo(function LineGraphRenderer({
  config,
}: RendererProps<LineGraphConfig>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const queryClient = useQueryClient();
  const dashboardTimeRange = useDashboardTimeRange();
  const isSingleSensor = config.sensorHumanIds.length === 1;
  const sensorId = isSingleSensor ? config.sensorHumanIds[0] : undefined;
  const showLatestValue = isSingleSensor && (config.showLatestValue ?? false);
  const showUnit = config.showUnit ?? true;
  const showLastUpdated = config.showLastUpdated ?? false;
  const latestValuePosition = config.latestValuePosition ?? 'right';

  // Use dashboard time range when mode is 'dashboard', otherwise use fixed config
  const timeRangeMode = config.timeRangeMode ?? 'fixed';
  const effectiveTimeRange: TimeRange =
    timeRangeMode === 'dashboard' ? dashboardTimeRange : config.timeRange;

  // Memoize time range to prevent infinite refetch loops
  const { startTime, endTime } = useMemo(() => {
    const now = Date.now();
    const rangeDuration = TIME_RANGE_MS[effectiveTimeRange];
    return {
      startTime: new Date(now - rangeDuration).toISOString(),
      endTime: new Date(now).toISOString(),
    };
  }, [effectiveTimeRange]);

  // Fetch history for ALL configured sensors in parallel
  const sensorQueries = useQueries({
    queries: config.sensorHumanIds.map((sensorId) =>
      getGetSensorHistoryQueryOptions(
        sensorId,
        {
          start: startTime,
          end: endTime,
          maxPoints: 500,
        },
        {
          query: {
            enabled: !!sensorId,
            refetchInterval: 60000,
          },
        }
      )
    ),
  });

  const isLoading = sensorQueries.some((q) => q.isLoading);
  const hasError = sensorQueries.some((q) => q.isError);

  const { data: latestSensorData, isError: isLatestError } = useGetSensor(sensorId ?? '', {
    query: {
      enabled: showLatestValue && !!sensorId,
      refetchInterval: 30000,
    },
  });

  const handleSensorEvent = useCallback(() => {
    if (!sensorId) return;
    queryClient.invalidateQueries({
      queryKey: getGetSensorQueryKey(sensorId),
    });
  }, [queryClient, sensorId]);

  useSensorStream({
    filter: sensorId ?? '',
    enabled: showLatestValue && !!sensorId,
    onEvent: handleSensorEvent,
  });

  // Fetch sensor metadata for labels
  const { data: sensorsData } = useListSensors();
  const sensorsMap = useMemo(() => {
    const map = new Map<string, SensorsSensor>();
    for (const sensor of sensorsData?.sensors ?? []) {
      if (sensor.humanId) {
        map.set(sensor.humanId, sensor);
      }
    }
    return map;
  }, [sensorsData?.sensors]);

  // Create separate datasets for each sensor
  const chartDatasets: SensorDataset[] = useMemo(() => {
    return config.sensorHumanIds.map((sensorId, index) => {
      const query = sensorQueries[index];
      const sensor = sensorsMap.get(sensorId);
      const values = query?.data?.values ?? [];

      return {
        label: sensor?.name || sensorId,
        color: sensor?.unitColor || undefined,
        data: values.map((v) => ({
          timestamp: v.timestamp ?? '',
          value: v.value ?? null,
        })),
      };
    });
  }, [config.sensorHumanIds, sensorQueries, sensorsMap]);

  if (config.sensorHumanIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No sensors configured</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Failed to load sensor data</p>
      </div>
    );
  }

  const latestSensor = isLatestError ? undefined : latestSensorData?.sensor;
  const latestValueText = formatValue(latestSensor?.value ?? null);
  const latestValueLength = latestValueText.length;
  const latestValueSizeClass =
    latestValueLength <= 3 ? 'text-3xl' : latestValueLength <= 5 ? 'text-2xl' : 'text-xl';
  const latestUnitSizeClass =
    latestValueLength <= 3 ? 'text-lg' : latestValueLength <= 5 ? 'text-base' : 'text-sm';

  const latestValuePanel = showLatestValue ? (
    <div className="flex w-28 flex-shrink-0 flex-col items-center justify-center px-2">
      <div className="flex items-baseline">
        <span
          className={`${latestValueSizeClass} font-semibold tabular-nums text-gray-900 dark:text-white`}
          style={latestSensor?.unitColor ? { color: latestSensor.unitColor } : undefined}
        >
          {latestValueText}
        </span>
        {showUnit && latestSensor?.unitSymbol ? (
          <span className={`ml-1.5 ${latestUnitSizeClass} text-gray-500 dark:text-zinc-400`}>
            {latestSensor.unitSymbol}
          </span>
        ) : null}
      </div>
      {showLastUpdated && latestSensor?.lastSeen ? (
        <span className="mt-1 text-[10px] text-gray-400 dark:text-zinc-500">
          {formatRelativeTime(latestSensor.lastSeen)}
        </span>
      ) : null}
    </div>
  ) : null;

  const chartContent = (
    <SensorChart
      data={[]}
      datasets={chartDatasets}
      startTime={startTime}
      endTime={endTime}
      height={undefined}
      isLoading={isLoading}
    />
  );

  const chartLayout = (
    <>
      {showLatestValue ? (
        <>
          {latestValuePosition === 'left' ? latestValuePanel : null}
          <div className="min-h-0 flex-1">{chartContent}</div>
          {latestValuePosition === 'right' ? latestValuePanel : null}
        </>
      ) : (
        <div className="min-h-0 flex-1">{chartContent}</div>
      )}
    </>
  );

  return (
    <>
      <div
        className={`group relative flex h-full cursor-pointer ${
          showLatestValue ? 'flex-row gap-2' : 'flex-col'
        }`}
        onClick={() => setIsExpanded(true)}
      >
        {/* Expand hint */}
        <div className="absolute right-2 top-2 z-10 rounded-md bg-black/50 p-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Expand className="h-3.5 w-3.5 text-white" />
        </div>
        {chartLayout}
      </div>

      {/* Fullscreen modal */}
      <Modal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        title="Line Graph"
        subtitle={`${config.sensorHumanIds.length} sensor${config.sensorHumanIds.length > 1 ? 's' : ''} · ${effectiveTimeRange}`}
        size="xl"
      >
        <div className="h-[60vh]">
          <div className={`flex h-full ${showLatestValue ? 'flex-row gap-3' : 'flex-col'}`}>
            {chartLayout}
          </div>
        </div>
      </Modal>
    </>
  );
});

/**
 * Line Graph widget definition
 */
export const lineGraphWidget: WidgetDefinition<LineGraphConfig> = {
  type: 'line-graph',
  displayName: 'Line Graph',
  description: 'Display sensor history as a line chart',
  icon: LineChart,
  defaultSize: { w: 4, h: 3 },
  minSize: { w: 3, h: 2 },
  maxSize: { w: 12, h: 6 },
  configSchema: lineGraphConfigSchema,
  defaultConfig: {
    sensorHumanIds: [],
    timeRangeMode: 'dashboard',
    timeRange: '24h',
    showLatestValue: false,
    showUnit: true,
    showLastUpdated: false,
    latestValuePosition: 'right',
  },
  ConfigForm: LineGraphConfigForm,
  Renderer: LineGraphRenderer,
  supportsTimeRange: true,
};
